// src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Link as MuiLink, CircularProgress, Stack, Divider, Chip, Badge } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy, limit, addDoc, getDocs, documentId, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { normalizeRandevuDurum, getRandevuDurumColor } from './AppointmentConstants';
import { yeniGorusmeDurumlari } from './PreInterviewConstants';
import { formatDistanceToNow, isPast, isBefore, startOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import VadesiYaklasanlar from './VadesiYaklasanlar';
import YaklasanTaksitler from './YaklasanTaksitler';
import CustomerFormModal from './CustomerFormModal';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PostAddIcon from '@mui/icons-material/PostAdd';
import GarageIcon from '@mui/icons-material/Garage';
import PaymentIcon from '@mui/icons-material/Payment';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// Bugünkü randevu kartında gösterilmeyecek durumlar
const KAPANMIS_RANDEVU_DURUMLARI = ['Tamamlandı', 'İptal'];

// Garaj ekranıyla AYNI kanonik durum seti (Garaj.js:23 ile birebir).
// Eski liste "Beklemede" (hiçbir kod yolunun yazmadığı, yalnız 2 legacy kayıtta
// duran değer) içeriyor, buna karşılık "Teslime Hazır"ı — teslim öncesi en kritik
// anı — İÇERMİYORDU: teslime hazır araçlar dashboard'da hiç görünmüyordu.
const GARAJ_DURUMLARI = ['İşlemde', 'Tamamlandı', 'Teslime Hazır'];

// Dashboard'daki hiçbir dinleyici sınırsız okumaz. Sayaç bu tavana dayanırsa
// başlıkta "60+" gösterilir — sessiz kırpma yok.
const SORGU_LIMITI = 60;
const LISTE_LIMITI = 6;

const sayacEtiketi = (liste) => (liste.length === SORGU_LIMITI ? `${SORGU_LIMITI}+` : liste.length);

const formatCurrency = (amount, finansGizli) => {
    if (finansGizli) return '**** ₺';
    return (amount || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
};

const getLogoPath = (markaModel) => {
    if (!markaModel || typeof markaModel !== 'string' || markaModel.trim().length === 0) {
        return '/logos/default.svg';
    }
    const logoName = markaModel.split(' ')[0].toLowerCase().replace(' ', '-');
    return `/logos/${logoName}.svg`;
};

// Bugün Teslim Alınacak Araç Kartı
const TodayPickupCard = ({ appointment }) => (
    <Paper component={RouterLink} to={`/randevu/${appointment.id}`} sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center', textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: '#fff' }}>
        <Box sx={{ width: 50, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', borderRadius: 1, p: 0.5 }}>
            <Box component="img" src={getLogoPath([appointment.aracMarka, appointment.aracModel].filter(Boolean).join(' ') || appointment.aracMarkaModel)} onError={(e) => { e.currentTarget.src = '/logos/default.svg'; }} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
            {/* appointments şemasında alanlar customerName / aracMarka+aracModel; iş emri alan adları burada boş kalıyordu */}
            <Typography variant="body2" fontWeight="bold">{appointment.aracPlaka || 'Plaka Yok'}</Typography>
            <Typography variant="caption" color="text.secondary">{appointment.customerName || appointment.musteriAdSoyad || '-'}</Typography>
        </Box>
        <Chip label={appointment.durum} size="small" color={getRandevuDurumColor(appointment.durum)} variant="outlined" />
    </Paper>
);

// Garaj Araç Kartı
const GaragePreviewCard = ({ workOrder }) => {
    const [kalanSure, setKalanSure] = useState('');
    useEffect(() => {
        if (workOrder?.teslimTarihi) {
            const teslimTarihiDate = workOrder.teslimTarihi.toDate();
            if (isPast(teslimTarihiDate) && !['Teslime Hazır', 'Tamamlandı'].includes(workOrder.durum)) {
                setKalanSure("Gecikti");
                return;
            }
            setKalanSure(formatDistanceToNow(teslimTarihiDate, { addSuffix: true, locale: tr }));
        }
    }, [workOrder]);

    return (
        <Paper component={RouterLink} to={`/is-emri/${workOrder.id}`} sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center', textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: '#fff' }}>
            <Box sx={{ width: 60, height: 45, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', borderRadius: 1, p: 0.5 }}>
                <Box component="img" src={getLogoPath(workOrder.aracMarkaModel)} onError={(e) => { e.currentTarget.src = '/logos/default.svg'; }} alt={workOrder.aracPlaka} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
            <Box>
                <Typography variant="body2" fontWeight="bold">{workOrder.aracPlaka}</Typography>
                <Typography variant="caption" color="text.secondary">{workOrder.musteriAdSoyad}</Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color={kalanSure === "Gecikti" ? "error" : "primary.main"} fontWeight="bold">{kalanSure}</Typography>
        </Paper>
    );
};

// Ödeme Bekleyen Kartı
const DuePaymentCard = ({ workOrder, finansGizli }) => (
    <MuiLink component={RouterLink} to={`/is-emri/${workOrder.id}`} sx={{ textDecoration: 'none' }}>
        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: '#fff' }}>
            <Box>
                <Typography variant="body2" fontWeight="bold">{workOrder.musteriAdSoyad}</Typography>
                <Typography variant="caption" color="text.secondary">{workOrder.aracPlaka}</Typography>
            </Box>
            <Typography color="error.main" fontWeight="bold">
                {formatCurrency(workOrder.kalanTutar, finansGizli)}
            </Typography>
        </Paper>
    </MuiLink>
);

const Dashboard = () => {
    const [todayPickups, setTodayPickups] = useState([]);
    const [garageOrders, setGarageOrders] = useState([]);
    const [dueOrders, setDueOrders] = useState([]);
    const [manuelBorclular, setManuelBorclular] = useState([]);
    const [yeniGorusmeSayisi, setYeniGorusmeSayisi] = useState(0);
    const [loading, setLoading] = useState(true);
    const { currentUser, finansGizli } = useAuth();
    const navigate = useNavigate();

    const isUsta = currentUser?.role === 'Usta';

    const [isCustomerModalOpen, setCustomerModalOpen] = useState(false);

    useEffect(() => {
        if (!currentUser) return;

        const aboneler = [];

        // Garajdaki araçlar — Usta dahil HER rolde açılır (Usta'nın tek kartı bu).
        // "Bugün Teslim Edilecek" ve "Geciken" sayıları da bu tek sorgudan türer;
        // ayrı dinleyici açmaya gerek yok (durum+teslimTarihi composite index var).
        aboneler.push(onSnapshot(
            query(
                collection(db, "workOrders"),
                where("durum", "in", GARAJ_DURUMLARI),
                orderBy("teslimTarihi", "asc"),
                limit(SORGU_LIMITI)
            ),
            (snapshot) => {
                setGarageOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setLoading(false);
            },
            (error) => { console.error("Garajdaki araçlar çekilemedi:", error); setLoading(false); }
        ));

        // Usta finansal veri ve randevu görmez → o dinleyiciler HİÇ açılmaz.
        if (isUsta) {
            setLoading(false);
            return () => aboneler.forEach(u => u());
        }

        // 1. Bugün teslim alınacak araçlar (bugünkü randevular)
        // Durum listesi elle yazılmıyor: sorgu bugünün tarih aralığına göre yapılır,
        // durum süzmesi normalize edilmiş değer üzerinden client-side'dır. Eski
        // `where("durum","in",[...])` kalıbı durum alanı olmayan v1 randevularını gizliyordu.
        const gunBasi = new Date(); gunBasi.setHours(0, 0, 0, 0);
        const gunSonu = new Date(); gunSonu.setHours(23, 59, 59, 999);
        aboneler.push(onSnapshot(
            query(
                collection(db, "appointments"),
                where("start", ">=", Timestamp.fromDate(gunBasi)),
                where("start", "<=", Timestamp.fromDate(gunSonu)),
                orderBy("start", "asc"),
                limit(SORGU_LIMITI)
            ),
            (snapshot) => {
                const today = snapshot.docs
                    .map(doc => ({ ...doc.data(), id: doc.id }))
                    .map(app => ({ ...app, durum: normalizeRandevuDurum(app.durum) }))
                    .filter(app => !KAPANMIS_RANDEVU_DURUMLARI.includes(app.durum));
                setTodayPickups(today);
            },
            (error) => console.error("Bugünkü randevular çekilemedi:", error)
        ));

        // 2. Ödeme bekleyenler (iş emri kalanları)
        aboneler.push(onSnapshot(
            query(
                collection(db, "workOrders"),
                where("kalanTutar", ">", 0),
                orderBy("kalanTutar", "desc"),
                limit(SORGU_LIMITI)
            ),
            (snapshot) => setDueOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))),
            (error) => console.error("Ödeme bekleyenler çekilemedi:", error)
        ));

        // 3. 'Yeni Görüşme' rozeti — ön görüşme kısayolunda kaç kayıt beklediğini gösterir
        aboneler.push(onSnapshot(
            query(
                collection(db, "preInterviews"),
                where("durum", "in", yeniGorusmeDurumlari),
                limit(SORGU_LIMITI)
            ),
            (snapshot) => setYeniGorusmeSayisi(snapshot.size),
            (error) => console.error("Ön görüşme sayısı çekilemedi:", error)
        ));

        // 4. Manuel cari borçlar — "Ödeme Bekleyenler" yalnız iş emri kalanlarına bakar,
        // elle girilen borçlar (transactions type='borc') o listeye HİÇ girmiyordu.
        // Tek seferlik okuma: bu kanal nadiren değişir, sürekli dinleyiciye değmez.
        (async () => {
            try {
                const borcSnap = await getDocs(query(
                    collection(db, "transactions"),
                    where("type", "==", "borc"),
                    limit(SORGU_LIMITI)
                ));
                const musteriIdleri = [...new Set(
                    borcSnap.docs.map(d => d.data().customerId || d.data().musteriId).filter(Boolean)
                )].slice(0, 30);
                if (musteriIdleri.length === 0) return;

                // `in` sorgusu 10'arlı parçalara bölünür (SDK sınırı).
                const parcalar = [];
                for (let i = 0; i < musteriIdleri.length; i += 10) parcalar.push(musteriIdleri.slice(i, i + 10));
                const sonuclar = await Promise.all(parcalar.map(parca =>
                    getDocs(query(collection(db, "customers"), where(documentId(), "in", parca)))
                ));
                const borclular = sonuclar
                    .flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })))
                    .filter(c => (c.toplamBakiye || 0) > 0);
                setManuelBorclular(borclular);
            } catch (error) {
                console.error("Manuel cari borçlar çekilemedi:", error);
            }
        })();

        return () => aboneler.forEach(u => u());
    }, [currentUser, isUsta]);

    // Garaj sorgusundan türeyen alt kümeler — ek okuma yok.
    const bugunTeslimEdilecek = garageOrders.filter(o => o.durum === 'Teslime Hazır');
    // Geciken = teslim tarihi BUGÜNDEN ÖNCE olan, hâlâ teslime hazır olmayan işler.
    // (Gün bazlı: bugün teslim edilecek bir iş "gecikmiş" sayılmaz.)
    const gecikenler = garageOrders.filter(o =>
        o.durum !== 'Teslime Hazır' &&
        o.teslimTarihi?.toDate &&
        isBefore(o.teslimTarihi.toDate(), startOfDay(new Date()))
    );
    const manuelBorcToplami = manuelBorclular.reduce((s, c) => s + (c.toplamBakiye || 0), 0);

    const handleAddCustomer = async (customerData, vehicleData) => {
        try {
            const newCustomerRef = await addDoc(collection(db, "customers"), { ...customerData, eklenmeTarihi: serverTimestamp() });
            if (vehicleData) {
                const vehicleQuery = query(collection(db, 'vehicles'), where("plaka", "==", vehicleData.plaka));
                if (!(await getDocs(vehicleQuery)).empty) { alert('Bu plaka zaten sisteme kayıtlı.'); return; }
                await addDoc(collection(db, "vehicles"), { ...vehicleData, musteriId: newCustomerRef.id, musteriAdSoyad: customerData.adSoyad, eklenmeTarihi: serverTimestamp() });
            }
            alert("Müşteri başarıyla eklendi!");
        } catch (e) { console.error("Müşteri eklenirken hata: ", e); }
    };

    if (loading || !currentUser) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <CustomerFormModal isOpen={isCustomerModalOpen} onClose={() => setCustomerModalOpen(false)} onAddCustomer={handleAddCustomer} />

            {currentUser.role !== 'Usta' && (
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={6} md={3}>
                            <Badge
                                badgeContent={yeniGorusmeSayisi === SORGU_LIMITI ? `${SORGU_LIMITI}+` : yeniGorusmeSayisi}
                                color="error"
                                max={999}
                                sx={{ width: '100%', '& .MuiBadge-badge': { top: 8, right: 12 } }}
                                title={`${yeniGorusmeSayisi} bekleyen yeni görüşme`}
                            >
                                <Box className="ios-glass-button orange" sx={{ width: '100%' }} onClick={() => navigate('/on-gorusme/yeni')}>
                                    <Box className="icon-container"><SupportAgentIcon sx={{ fontSize: 32 }} /></Box>
                                    <Typography className="button-label">Ön Görüşme</Typography>
                                </Box>
                            </Badge>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Box className="ios-glass-button blue" onClick={() => navigate('/randevular')}>
                                <Box className="icon-container"><EventIcon sx={{ fontSize: 32 }} /></Box>
                                <Typography className="button-label">Randevular</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Box className="ios-glass-button green" onClick={() => setCustomerModalOpen(true)}>
                                <Box className="icon-container"><PersonAddIcon sx={{ fontSize: 32 }} /></Box>
                                <Typography className="button-label">Müşteri Ekle</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3}>
                            <Box className="ios-glass-button purple" onClick={() => navigate('/is-emri-olustur')}>
                                <Box className="icon-container"><PostAddIcon sx={{ fontSize: 32 }} /></Box>
                                <Typography className="button-label">İş Emri Oluştur</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Grid container spacing={3}>
                {isUsta ? (
                    <Grid item xs={12}><Paper sx={{ p: 2, height: '100%' }}><Typography variant="h6" gutterBottom>Garajdaki Aktif İşler ({sayacEtiketi(garageOrders)})</Typography><Divider /><Stack spacing={1.5} sx={{ mt: 2 }}>{garageOrders.length > 0 ? garageOrders.map(order => <GaragePreviewCard key={order.id} workOrder={order} />) : <Typography color="text.secondary">Garajda bekleyen araç yok.</Typography>}</Stack></Paper></Grid>
                ) : (
                    <>
                        {/* 1. Bugün Teslim Alınacak Araçlar (randevu) */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DirectionsCarIcon color="warning" />
                                    <Typography variant="h6">Bugün Teslim Alınacak ({sayacEtiketi(todayPickups)})</Typography>
                                </Box>
                                <Divider />
                                <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1 }}>
                                    {todayPickups.length > 0 ? (
                                        <>
                                            {todayPickups.slice(0, LISTE_LIMITI).map(app => (
                                                <TodayPickupCard key={app.id} appointment={app} />
                                            ))}
                                            {todayPickups.length > LISTE_LIMITI && (
                                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                    +{todayPickups.length - LISTE_LIMITI} randevu daha
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>Bugün teslim alınacak araç yok.</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* 2. Teslime Hazır — teslim öncesi en kritik an; eski durum seti bunu hiç göstermiyordu */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <AssignmentTurnedInIcon color="success" />
                                    <Typography variant="h6">Teslime Hazır ({bugunTeslimEdilecek.length})</Typography>
                                </Box>
                                {gecikenler.length > 0 && (
                                    <Chip size="small" color="error" variant="outlined" sx={{ alignSelf: 'flex-start', mb: 1 }}
                                        label={`${gecikenler.length} iş emri gecikti`} />
                                )}
                                <Divider />
                                <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1 }}>
                                    {bugunTeslimEdilecek.length > 0 ? (
                                        <>
                                            {bugunTeslimEdilecek.slice(0, LISTE_LIMITI).map(order => <GaragePreviewCard key={order.id} workOrder={order} />)}
                                            {bugunTeslimEdilecek.length > LISTE_LIMITI && (
                                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                    +{bugunTeslimEdilecek.length - LISTE_LIMITI} araç daha
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>Teslime hazır araç yok.</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* 3. Garajdaki Araçlar */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <GarageIcon color="primary" />
                                    <Typography variant="h6">Garajdaki Araçlar ({sayacEtiketi(garageOrders)})</Typography>
                                </Box>
                                <Divider />
                                <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1 }}>
                                    {garageOrders.length > 0 ? (
                                        <>
                                            {garageOrders.slice(0, LISTE_LIMITI).map(order => <GaragePreviewCard key={order.id} workOrder={order} />)}
                                            {garageOrders.length > LISTE_LIMITI && (
                                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                    +{garageOrders.length - LISTE_LIMITI} araç daha
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>Garajda araç yok.</Typography>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* 4. Ödeme Bekleyenler */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <PaymentIcon color="error" />
                                    <Typography variant="h6">Ödeme Bekleyenler ({sayacEtiketi(dueOrders)})</Typography>
                                </Box>
                                <Divider />
                                <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1 }}>
                                    {dueOrders.length > 0 ? (
                                        <>
                                            {dueOrders.slice(0, LISTE_LIMITI).map(order => <DuePaymentCard key={order.id} workOrder={order} finansGizli={finansGizli} />)}
                                            {dueOrders.length > LISTE_LIMITI && (
                                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                    +{dueOrders.length - LISTE_LIMITI} iş emri daha
                                                </Typography>
                                            )}
                                        </>
                                    ) : (
                                        <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>Ödeme bekleyen iş emri yok.</Typography>
                                    )}
                                </Stack>
                                {/* Bu liste yalnız İŞ EMRİ kalanlarını gösterir; elle girilen cari borçlar
                                    (transactions type='borc') buraya girmez — eksik görünmesin diye ayrıca yazılır. */}
                                {manuelBorclular.length > 0 && (
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                                        <MuiLink component={RouterLink} to="/cari-hesaplar" variant="caption" color="error.main">
                                            + {manuelBorclular.length} müşteride iş emrine bağlı olmayan cari borç
                                            {' '}({formatCurrency(manuelBorcToplami, finansGizli)}) — Cari Hesaplar
                                        </MuiLink>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* 5. Teslimi Yaklaşanlar (iş emri teslim tarihi) */}
                        <Grid item xs={12} md={6} lg={4}><VadesiYaklasanlar finansGizli={finansGizli} /></Grid>

                        {/* 6. Yaklaşan Taksitler (gerçek vade — scheduledPayments) */}
                        <Grid item xs={12} md={6} lg={4}><YaklasanTaksitler finansGizli={finansGizli} /></Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default Dashboard;